import type { StepDefinition } from '../types'

const shortHelp = {
  notFound: 'Pare nesta tela e mostre o passo ao professor.',
  differentResult:
    'Não apague o que já fez. Mostre o resultado ao professor antes de tentar novamente.',
  askTeacherWhen:
    'um programa não abrir, uma pasta não aparecer ou a tela ficar diferente do exemplo.',
}

export const steps: StepDefinition[] = [
  {
    id: 'inicio',
    kind: 'context',
    block: 'Início',
    minutes: 5,
    shortTitle: 'Sua missão',
    title: 'Prepare uma estação de trabalho',
    phase: 'Abertura',
    objective:
      'Você recebeu uma estação de trabalho que precisa ficar organizada e pronta para uso.',
    action: 'Informe seu nome e conheça a entrega que será produzida.',
    instructions: [
      'Digite seu primeiro nome no campo abaixo.',
      'Leia a missão: organizar arquivos, produzir documentos e verificar a rede.',
      'Mantenha este site aberto. Use Alt + Tab para alternar entre o guia e os programas.',
    ],
    expected: [
      'Ao final, você entregará uma pasta organizada',
      'com Word, Excel, rede, PowerPoint e evidências.',
    ],
    checklist: [],
    visual: 'briefing',
    fields: [
      {
        key: 'student_name',
        label: 'Seu primeiro nome',
        kind: 'text',
        placeholder: 'Exemplo: Ana',
        help: 'Use o nome que o professor orientou.',
        required: true,
      },
    ],
    support: shortHelp,
  },
  {
    id: 'organizacao-contexto',
    kind: 'context',
    block: 'Organização e Windows',
    shortTitle: 'Organização',
    title: 'Primeiro, organize o local de trabalho',
    phase: 'Bloco 1',
    objective:
      'Arquivos bem organizados permitem que outra pessoa encontre e confira o trabalho.',
    action: 'Prepare-se para criar uma única estrutura de pastas.',
    instructions: [
      'Abra o Explorador de Arquivos.',
      'Localize a pasta Documentos.',
      'Na próxima tela, siga exatamente os nomes apresentados.',
    ],
    expected: ['Explorador de Arquivos aberto em Documentos.'],
    checklist: [],
    visual: 'folder',
    support: shortHelp,
  },
  {
    id: 'organizacao-tarefa',
    kind: 'task',
    block: 'Organização e Windows',
    minutes: 20,
    shortTitle: 'Pastas e Windows',
    title: 'Crie as pastas e registre o Windows',
    phase: 'Bloco 1',
    objective:
      'A equipe precisa de uma estrutura única para guardar todos os arquivos da atividade.',
    action: 'Crie a estrutura abaixo e salve duas capturas na pasta de evidências.',
    instructions: [
      'Em Documentos, crie a pasta {{PASTA_PRINCIPAL}}.',
      'Dentro dela, crie: 01_DOCUMENTO, 02_PLANILHA, 03_REDE, 04_APRESENTACAO e 05_EVIDENCIAS.',
      'Deixe as cinco subpastas visíveis. Pressione Windows + Shift + S e selecione somente a área das pastas.',
      'Abra a notificação da captura, pressione Ctrl + S, escolha 05_EVIDENCIAS e salve como E01_PASTAS.png.',
      'Pressione Windows + R, digite msinfo32 e pressione Enter.',
      'Localize Nome do SO, Versão e Tipo de sistema. Pressione Windows + Shift + S e selecione esses três campos.',
      'Abra a notificação, pressione Ctrl + S e salve em 05_EVIDENCIAS como E02_WINDOWS.png.',
    ],
    expected: [
      '{{PASTA_PRINCIPAL}}',
      '├── 01_DOCUMENTO',
      '├── 02_PLANILHA',
      '├── 03_REDE',
      '├── 04_APRESENTACAO',
      '└── 05_EVIDENCIAS',
    ],
    checklist: [],
    visual: 'system',
    warning:
      'Não altere nenhuma configuração do Windows. Apenas consulte e capture as informações.',
    support: {
      notFound:
        'Confira se você está em Documentos e se os cinco nomes estão iguais ao modelo.',
      differentResult:
        'Se a captura abriu, use Ctrl + S. Se não abriu, repita Windows + Shift + S e selecione a área novamente.',
      askTeacherWhen:
        'a notificação da captura não aparecer, o msinfo32 não abrir ou um dos três campos não existir.',
    },
    copyables: [
      {
        label: 'Nome da pasta principal',
        value: '{{PASTA_PRINCIPAL}}',
      },
      {
        label: 'Nomes das subpastas',
        value:
          '01_DOCUMENTO\n02_PLANILHA\n03_REDE\n04_APRESENTACAO\n05_EVIDENCIAS',
      },
    ],
  },
  {
    id: 'word-contexto',
    kind: 'context',
    block: 'Microsoft Word',
    shortTitle: 'Documento',
    title: 'Transforme os dados em um relatório',
    phase: 'Bloco 2',
    objective:
      'A equipe precisa de um documento curto para saber quem preparou a estação e quais informações foram encontradas.',
    action: 'Abra o Microsoft Word e prepare-se para produzir um relatório de uma página.',
    instructions: [
      'Abra o Microsoft Word.',
      'Escolha Documento em branco.',
      'Na próxima tela, copie a estrutura indicada e aplique a formatação.',
    ],
    expected: ['Microsoft Word aberto em um documento em branco.'],
    checklist: [],
    visual: 'document',
    support: shortHelp,
  },
  {
    id: 'word-tarefa',
    kind: 'task',
    block: 'Microsoft Word',
    minutes: 30,
    shortTitle: 'Relatório Word',
    title: 'Produza o relatório da estação',
    phase: 'Bloco 2',
    objective:
      'O relatório deve registrar a organização realizada e os dados consultados no Windows.',
    action: 'Crie, formate e exporte o relatório seguindo os passos.',
    instructions: [
      'Abra o modelo abaixo, clique em Copiar e cole no Word com Ctrl + V.',
      'Troque os campos [valor] pelos dados do msinfo32 e digite a data no formato dia/mês/ano.',
      'Selecione somente o título RELATÓRIO DA ESTAÇÃO DE TRABALHO e use tamanho 18, negrito e centralizado.',
      'Selecione os dois subtítulos e use tamanho 14 em negrito. Selecione o texto restante e use tamanho 11.',
      'Clique em Inserir → Rodapé → Em branco e digite: Recuperação ICP — Operador de Computador e Redes.',
      'Clique em Arquivo → Salvar como → Procurar. Escolha 01_DOCUMENTO e salve como Relatorio_da_Estacao.docx.',
      'Clique em Arquivo → Exportar → Criar PDF/XPS → Criar PDF/XPS. Salve em 01_DOCUMENTO como Relatorio_da_Estacao.pdf.',
      'Abra 01_DOCUMENTO no Explorador e confirme que os arquivos DOCX e PDF aparecem juntos.',
    ],
    expected: [
      '01_DOCUMENTO',
      '├── Relatorio_da_Estacao.docx',
      '└── Relatorio_da_Estacao.pdf',
    ],
    checklist: [],
    visual: 'document',
    support: {
      notFound:
        'Use Alt + Tab para voltar ao site. Confira o nome do arquivo e se a pasta aberta é 01_DOCUMENTO.',
      differentResult:
        'Se não houver Exportar, use Arquivo → Salvar como e escolha PDF no campo Tipo.',
      askTeacherWhen:
        'o Word não abrir, a opção Rodapé não aparecer ou o PDF não surgir na pasta.',
    },
    copyables: [
      {
        label: 'Estrutura do relatório',
        value:
          'RELATÓRIO DA ESTAÇÃO DE TRABALHO\n\nNome: {{NOME}}\nData: [data de hoje]\n\nORGANIZAÇÃO REALIZADA\n• 01_DOCUMENTO\n• 02_PLANILHA\n• 03_REDE\n• 04_APRESENTACAO\n• 05_EVIDENCIAS\n\nINFORMAÇÕES DO WINDOWS\nNome do SO: [valor]\nVersão: [valor]\nTipo de sistema: [valor]',
      },
    ],
  },
  {
    id: 'excel-contexto',
    kind: 'context',
    block: 'Microsoft Excel',
    shortTitle: 'Controle',
    title: 'Organize os equipamentos em uma planilha',
    phase: 'Bloco 3',
    objective:
      'A equipe precisa visualizar quantos equipamentos estão disponíveis e qual item aparece em maior quantidade.',
    action: 'Abra o Microsoft Excel e prepare uma planilha em branco.',
    instructions: [
      'Abra o Microsoft Excel.',
      'Escolha Pasta de trabalho em branco.',
      'Na próxima tela, digite os dados exatamente como aparecem.',
    ],
    expected: ['Microsoft Excel aberto em uma planilha vazia.'],
    checklist: [],
    visual: 'spreadsheet',
    support: shortHelp,
  },
  {
    id: 'excel-tarefa',
    kind: 'task',
    block: 'Microsoft Excel',
    minutes: 35,
    shortTitle: 'Planilha Excel',
    title: 'Monte a planilha e o gráfico',
    phase: 'Bloco 3',
    objective:
      'A planilha deve permitir uma leitura rápida dos equipamentos registrados.',
    action: 'Digite os dados, use duas fórmulas e crie um gráfico.',
    instructions: [
      'Em A1 digite Equipamento e em B1 digite Quantidade.',
      'Preencha: Monitor 2; Teclado 4; Mouse 3; Cabo de rede 6; Headset 2.',
      'Em A7 digite Total e em B7 use =SOMA(B2:B6).',
      'Em A8 digite Média e em B8 use =MÉDIA(B2:B6).',
      'Confirme B7 = 17 e B8 = 3,4. Se algum valor estiver diferente, confira B2:B6 antes de continuar.',
      'Selecione A1:B8 e aplique Todas as bordas. Depois, selecione A1:B1, aplique negrito e preenchimento azul-claro.',
      'Selecione A1:B6. Clique em Inserir → Gráfico de Colunas → Colunas agrupadas e use o título Quantidade de equipamentos.',
      'Clique em Arquivo → Salvar como → Procurar. Escolha 02_PLANILHA e salve como Controle_de_Equipamentos.xlsx.',
      'Deixe a tabela e o gráfico visíveis. Pressione Windows + Shift + S, selecione os dois, abra a notificação e pressione Ctrl + S.',
      'Escolha 05_EVIDENCIAS e salve a captura como E03_PLANILHA.png.',
    ],
    expected: [
      'Total: 17',
      'Média: 3,4',
      'Arquivo: 02_PLANILHA\\Controle_de_Equipamentos.xlsx',
      'Captura: 05_EVIDENCIAS\\E03_PLANILHA.png',
    ],
    checklist: [],
    visual: 'spreadsheet',
    support: {
      notFound:
        'Clique em uma célula e confira o endereço mostrado à esquerda da barra de fórmulas.',
      differentResult:
        'Se Total não for 17 ou Média não for 3,4, confira os números de B2 até B6 antes de criar o gráfico.',
      askTeacherWhen:
        'a fórmula mostrar erro, o menu Colunas agrupadas não aparecer ou a captura não abrir.',
    },
    copyables: [
      {
        label: 'Dados da planilha',
        value:
          'Equipamento\tQuantidade\nMonitor\t2\nTeclado\t4\nMouse\t3\nCabo de rede\t6\nHeadset\t2',
      },
    ],
  },
  {
    id: 'rede-contexto',
    kind: 'context',
    block: 'Diagnóstico de rede',
    shortTitle: 'Diagnóstico',
    title: 'Verifique a comunicação da estação',
    phase: 'Bloco 4',
    objective:
      'Antes de pedir suporte, a equipe registra a configuração e executa testes simples de diagnóstico.',
    action: 'Abra o Prompt de Comando sem alterar nenhuma configuração.',
    instructions: [
      'Pressione Windows + R.',
      'Digite cmd e pressione Enter.',
      'Na próxima tela, execute um comando por vez.',
    ],
    expected: ['Prompt de Comando aberto, aguardando um comando.'],
    checklist: [],
    visual: 'terminal',
    warning:
      'Os comandos desta atividade apenas consultam ou testam a rede. Não altere configurações.',
    support: shortHelp,
  },
  {
    id: 'rede-tarefa',
    kind: 'task',
    block: 'Diagnóstico de rede',
    minutes: 25,
    shortTitle: 'Testes de rede',
    title: 'Execute e registre os testes',
    phase: 'Bloco 4',
    objective:
      'O registro deve mostrar a configuração da estação e o resultado dos três testes.',
    action: 'Execute os comandos e registre os resultados em um arquivo de texto.',
    instructions: [
      'Execute ipconfig /all. Use o bloco Ethernet ou Wi-Fi que tenha Endereço IPv4 e Gateway Padrão preenchidos. Se dois blocos tiverem esses dados, chame o professor.',
      'Abra o Bloco de Notas, abra o modelo abaixo, clique em Copiar e cole com Ctrl + V. Preencha IPv4, Máscara, Gateway e DNS.',
      'Execute ping 127.0.0.1. Registre com resposta somente se a linha tiver bytes= e TTL= sem a palavra “inacessível”. Se houver “inacessível” ou “esgotado o tempo”, registre sem resposta.',
      'Digite ping, dê um espaço e digite o número do Gateway Padrão encontrado. Exemplo: ping 192.168.0.1. Use a mesma regra: bytes= e TTL= sem erro significa com resposta.',
      'Execute nslookup senai.br. Registre resolveu somente se, depois de “Nome: senai.br”, aparecer Endereço ou Addresses. Não conte o endereço mostrado apenas ao lado de Servidor. “Não encontrado” ou tempo esgotado significa não resolveu.',
      'No Bloco de Notas, clique em Arquivo → Salvar como. Escolha 03_REDE e salve como Diagnostico_de_Rede.txt.',
      'Execute novamente ping 127.0.0.1 e deixe o resultado visível no Prompt.',
      'Pressione Windows + Shift + S, selecione o resultado, abra a notificação, pressione Ctrl + S e salve em 05_EVIDENCIAS como E04_REDE.png.',
    ],
    expected: [
      '03_REDE\\Diagnostico_de_Rede.txt',
      '05_EVIDENCIAS\\E04_REDE.png',
      'Sem alterações nas configurações da rede.',
    ],
    checklist: [],
    visual: 'network',
    support: {
      notFound:
        'No ipconfig, procure o bloco que tenha Endereço IPv4 e Gateway Padrão preenchidos. Ignore blocos com mídia desconectada.',
      differentResult:
        '“Resposta de ... Destino inacessível” conta como sem resposta. O endereço do Servidor no início do nslookup não prova que senai.br resolveu.',
      askTeacherWhen:
        'não houver Gateway Padrão, dois adaptadores parecerem conectados, o Prompt não abrir ou o comando não existir.',
    },
    copyables: [
      { label: '1. Consultar a rede', value: 'ipconfig /all' },
      { label: '2. Testar o próprio computador', value: 'ping 127.0.0.1' },
      { label: '3. Testar resolução de nome', value: 'nslookup senai.br' },
      {
        label: '4. Modelo do registro',
        value:
          'DIAGNÓSTICO DE REDE\nIPv4: [valor]\nMáscara: [valor]\nGateway padrão: [valor]\nServidor DNS: [valor]\nTeste de loopback: [com resposta/sem resposta]\nTeste do gateway: [com resposta/sem resposta]\nTeste de DNS: [resolveu/não resolveu]',
      },
    ],
  },
  {
    id: 'powerpoint-contexto',
    kind: 'context',
    block: 'Microsoft PowerPoint',
    shortTitle: 'Apresentação',
    title: 'Apresente o trabalho realizado',
    phase: 'Bloco 5',
    objective:
      'A equipe precisa de uma apresentação curta para compreender o que foi organizado e testado.',
    action: 'Abra o Microsoft PowerPoint e prepare uma apresentação em branco.',
    instructions: [
      'Abra o Microsoft PowerPoint.',
      'Escolha Apresentação em branco.',
      'Na próxima tela, produza quatro slides com o modelo indicado.',
    ],
    expected: ['Microsoft PowerPoint aberto em uma apresentação vazia.'],
    checklist: [],
    visual: 'presentation',
    support: shortHelp,
  },
  {
    id: 'powerpoint-tarefa',
    kind: 'task',
    block: 'Microsoft PowerPoint',
    minutes: 30,
    shortTitle: 'Slides PowerPoint',
    title: 'Crie uma apresentação de quatro slides',
    phase: 'Bloco 5',
    objective:
      'A apresentação deve resumir o trabalho sem repetir todo o conteúdo dos arquivos.',
    action: 'Monte quatro slides, salve o arquivo editável e exporte o PDF.',
    instructions: [
      'Clique em Design → Temas e escolha o primeiro tema “Office”. Use esse mesmo tema até o final.',
      'No slide 1, use o título Preparação da Estação de Trabalho e o subtítulo Responsável: {{NOME}}.',
      'Crie o slide 2 com o título Organização e a lista: 01_DOCUMENTO; 02_PLANILHA; 03_REDE; 04_APRESENTACAO; 05_EVIDENCIAS.',
      'Crie o slide 3 com o título Controle de Equipamentos. Clique em Inserir → Imagens → Este dispositivo e abra 05_EVIDENCIAS\\E03_PLANILHA.png.',
      'Crie o slide 4 com o título Diagnóstico de Rede e três linhas: Loopback: [resultado]; Gateway: [resultado]; DNS: [resultado]. Use os resultados do arquivo TXT.',
      'Selecione os títulos e use tamanho 32. Selecione listas e resultados e use tamanho 24.',
      'Clique em Arquivo → Salvar como → Procurar. Escolha 04_APRESENTACAO e salve como Resumo_da_Estacao.pptx.',
      'Clique em Arquivo → Exportar → Criar PDF/XPS → Criar PDF/XPS. Salve em 04_APRESENTACAO como Resumo_da_Estacao.pdf.',
    ],
    expected: [
      '04_APRESENTACAO',
      '├── Resumo_da_Estacao.pptx',
      '└── Resumo_da_Estacao.pdf',
      'Apresentação com 4 slides.',
    ],
    checklist: [],
    visual: 'presentation',
    support: {
      notFound:
        'Use Alt + Tab para conferir o texto desta tela. Para criar outro slide, use Página Inicial → Novo Slide.',
      differentResult:
        'Se não houver Exportar, use Arquivo → Salvar como e escolha PDF no campo Tipo.',
      askTeacherWhen:
        'o PowerPoint não abrir, a imagem não aparecer ou os quatro slides não forem mostrados na lateral.',
    },
    copyables: [
      {
        label: 'Texto dos quatro slides',
        value:
          'SLIDE 1\nPreparação da Estação de Trabalho\nResponsável: {{NOME}}\n\nSLIDE 2\nOrganização\n• 01_DOCUMENTO\n• 02_PLANILHA\n• 03_REDE\n• 04_APRESENTACAO\n• 05_EVIDENCIAS\n\nSLIDE 3\nControle de Equipamentos\nInserir: 05_EVIDENCIAS\\E03_PLANILHA.png\n\nSLIDE 4\nDiagnóstico de Rede\nLoopback: [com resposta/sem resposta]\nGateway: [com resposta/sem resposta]\nDNS: [resolveu/não resolveu]',
      },
    ],
  },
  {
    id: 'entrega-contexto',
    kind: 'context',
    block: 'Entrega',
    shortTitle: 'Conferência',
    title: 'Prepare a pasta para conferência',
    phase: 'Bloco 6',
    objective:
      'A entrega deve permitir que o professor abra os arquivos e confira o trabalho sem procurar itens perdidos.',
    action: 'Abra a pasta principal e compare os arquivos com a lista final.',
    instructions: [
      'Abra {{PASTA_PRINCIPAL}} no Explorador de Arquivos.',
      'Compare as cinco subpastas com a lista da próxima tela.',
      'Se um arquivo estiver ausente, volte ao programa correspondente e salve-o.',
    ],
    expected: ['Pasta principal aberta e pronta para a conferência final.'],
    checklist: [],
    visual: 'checklist',
    support: shortHelp,
  },
  {
    id: 'entrega-tarefa',
    kind: 'task',
    block: 'Entrega',
    minutes: 15,
    shortTitle: 'ZIP final',
    title: 'Confira e compacte a entrega',
    phase: 'Bloco 6',
    objective:
      'Os arquivos editáveis, PDFs e registros devem permanecer dentro da pasta principal.',
    action: 'Confira os arquivos e crie uma cópia compactada da pasta.',
    instructions: [
      'Confirme visualmente os dois arquivos do Word em 01_DOCUMENTO.',
      'Confirme o arquivo do Excel em 02_PLANILHA.',
      'Confirme Diagnostico_de_Rede.txt em 03_REDE.',
      'Confirme os dois arquivos do PowerPoint em 04_APRESENTACAO.',
      'Confirme E01_PASTAS.png, E02_WINDOWS.png, E03_PLANILHA.png e E04_REDE.png em 05_EVIDENCIAS.',
      'Volte para Documentos, clique com o botão direito em {{PASTA_PRINCIPAL}} e escolha Compactar para arquivo ZIP.',
      'Entregue {{PASTA_PRINCIPAL}}.zip conforme a orientação do professor.',
    ],
    expected: [
      '{{PASTA_PRINCIPAL}}.zip',
      'Pasta original preservada.',
      'Nenhum arquivo enviado automaticamente pelo site.',
    ],
    checklist: [],
    visual: 'package',
    warning:
      'Não apague a pasta original depois de criar o ZIP.',
    support: shortHelp,
  },
  {
    id: 'fim',
    kind: 'finish',
    block: 'Finalização',
    shortTitle: 'Final',
    title: 'Entrega pronta para o professor',
    phase: 'Final',
    objective:
      'O site apenas guiou o trabalho. A avaliação será feita nos arquivos produzidos no computador.',
    action: 'Avise ao professor que terminou e aguarde a conferência.',
    instructions: [
      'Mantenha a pasta original e o ZIP salvos.',
      'Não feche os programas se o professor ainda precisar conferir.',
      'Mostre esta tela ao professor.',
    ],
    expected: [
      'Word: DOCX + PDF',
      'Excel: XLSX + gráfico',
      'Rede: TXT + captura',
      'PowerPoint: PPTX + PDF',
      'Evidências: 4 capturas',
      'Entrega: pasta original + ZIP',
    ],
    checklist: [],
    visual: 'package',
    support: shortHelp,
  },
]

export const stepById = new Map(steps.map((step) => [step.id, step]))
